import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { BuilderConfirmationsService } from './builderconfirmations.service';

type Filter = 'all' | 'open' | 'done' | 'failed' | 'unopened';

@Component({
  selector: 'app-builderconfirmations',
  templateUrl: './builderconfirmations.component.html',
  styleUrls: ['./builderconfirmations.component.css']
})
export class BuilderConfirmationsComponent implements OnInit {

  date = BuilderConfirmationsComponent.todayET();
  includeAll = false;
  loading = false;
  error = '';
  updatedAt: Date = null;

  sessions: any[] = [];
  search = '';
  filter: Filter = 'all';
  filters: { key: Filter, label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Has no-response' },
    { key: 'done', label: 'Fully answered' },
    { key: 'failed', label: 'Failed text' },
    { key: 'unopened', label: 'Not opened' }
  ];

  stats: any = {};

  constructor(private service: BuilderConfirmationsService, private auth: AuthService, private router: Router) {}

  static todayET(): string {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(new Date());
    return p; // en-CA formats as YYYY-MM-DD
  }

  ngOnInit(): void {
    // Builder accounts don't get this page.
    this.auth.userProfile$.subscribe(profile => {
      const meta = profile && profile['https://rhv2.com.metadata'];
      if (meta && meta.role === 'Builder') { this.router.navigate(['/estar']); }
    });
    this.load();
  }

  load(): void {
    if (!this.date) { return; }
    this.loading = true;
    this.error = '';
    this.service.getAudit(this.date, this.includeAll).subscribe((res: any) => {
      this.loading = false;
      if (res.Error) { this.error = res.Message; this.sessions = []; }
      else { this.sessions = this.sortSessions(res.Sessions || []); }
      this.stats = this.computeStats(this.sessions);
      this.updatedAt = new Date();
    }, err => {
      this.loading = false;
      this.error = 'Could not load: ' + (err && err.message ? err.message : err);
    });
  }

  // Fewest answered (by share) first, then builder name.
  sortSessions(list: any[]): any[] {
    return list.slice().sort((a, b) => {
      const ra = a.jobs.length ? a.answered / a.jobs.length : 1;
      const rb = b.jobs.length ? b.answered / b.jobs.length : 1;
      return ra - rb || String(a.contact_name || '').localeCompare(String(b.contact_name || ''));
    });
  }

  isSent(s: any): boolean { return s.sms_status === 'sent'; }
  isFailed(s: any): boolean { return !!s.sms_status && s.sms_status.indexOf('failed') === 0; }

  computeStats(list: any[]): any {
    const sent = list.filter(s => this.isSent(s));
    const pct = (n: number) => sent.length ? Math.round(100 * n / sent.length) : 0;
    const opened = sent.filter(s => s.first_opened_at).length;
    const some = sent.filter(s => s.answered > 0).length;
    const all = sent.filter(s => s.jobs.length && s.answered === s.jobs.length).length;
    const jobs = [].concat(...list.map(s => s.jobs));
    const starts = (j: any, p: string) => (j.conf_status || '').indexOf(p) === 0;
    return {
      sent: sent.length,
      failed: list.filter(s => this.isFailed(s)).length,
      opened, openedPct: pct(opened),
      some, somePct: pct(some),
      all, allPct: pct(all),
      jobs: jobs.length,
      confirmed: jobs.filter(j => starts(j, 'Builder confirmed')).length,
      callFirst: jobs.filter(j => starts(j, 'Not confirmed')).length,
      resched: jobs.filter(j => starts(j, 'Reschedule')).length,
      none: jobs.filter(j => !j.conf_status).length,
      scheduleDates: Array.from(new Set(list.map(s => s.schedule_date))).sort()
    };
  }

  get visible(): any[] {
    const q = this.search.trim().toLowerCase();
    return this.sessions.filter(s => {
      if (this.filter === 'open' && !s.jobs.some(j => !j.conf_status)) { return false; }
      if (this.filter === 'done' && s.answered < s.jobs.length) { return false; }
      if (this.filter === 'failed' && !this.isFailed(s)) { return false; }
      if (this.filter === 'unopened' && s.first_opened_at) { return false; }
      if (!q) { return true; }
      const hay = [s.contact_name, s.phone].concat(...s.jobs.map(j => [j.job_id, j.job_type, j.address, j.subdivision_lot, j.conf_status]))
        .join(' ').toLowerCase();
      return hay.indexOf(q) !== -1;
    });
  }

  textLabel(s: any): string {
    if (this.isSent(s)) { return 'Sent'; }
    if (this.isFailed(s)) { return 'Failed'; }
    if (s.sms_status === 'linkonly') { return 'Link only'; }
    return s.sms_status || 'Not sent';
  }

  // Colored-dot class for a job's current status.
  statusClass(status: string): string {
    if (!status) { return 'st-none'; }
    if (status.indexOf('Builder confirmed') === 0) { return 'st-ok'; }
    if (status.indexOf('Not confirmed') === 0) { return 'st-call'; }
    if (status.indexOf('Reschedule') === 0) { return 'st-resched'; }
    return 'st-other';
  }

  // Builder cards start collapsed; expanded ids survive Refresh.
  expanded = new Set<number>();
  toggle(s: any): void { this.expanded.has(s.id) ? this.expanded.delete(s.id) : this.expanded.add(s.id); }
  expandAll(): void { this.visible.forEach(s => this.expanded.add(s.id)); }
  collapseAll(): void { this.expanded.clear(); }

  dayLabel(ymd: string): string {
    const d = new Date(ymd + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
  }
}
